import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { pageMeta, shareUrl } from "@/lib/seo";
import { formatLocalDate, parseLocalDate } from "@/lib/format-date";
import { freshness } from "@/lib/freshness";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useRef, useState } from "react";
import { endOrphanLiveActivitiesIfSafe } from "@/lib/native/live-activity";


import { useCachedTrip } from "@/lib/offline/trip-cache";
import { useOfflineRedirect } from "@/lib/offline/use-offline-redirect";
import roavaWordmark from "@/assets/bundled/roava-website-logo-transparent.png";

import { getMyTrip, getTripMembers, getTripDashboard, getTripInviteCode, regenerateTripInviteCode, submitMyTrip, unsubmitMyTrip } from "@/lib/trip.functions";
import { TripSwitcher } from "@/components/TripSwitcher";

import { SubmitTripDialog } from "@/components/SubmitTripDialog";
import { setTrackingState } from "@/lib/native/tracking-state";
import { stopActiveWatch } from "@/lib/native/active-watch";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MemberDetailSheet, type MemberLite } from "@/components/MemberDetailSheet";
import { SaveTripToHealthButton } from "@/components/SaveTripToHealthButton";
import { FamilyFollowCard } from "@/components/family/FamilyFollowCard";
import { TrackingHealthChip } from "@/components/tracking/TrackingHealthChip";
import { FixStuckUploadsSheet } from "@/components/FixStuckUploadsSheet";
import { ManualGpsButton } from "@/components/ManualGpsButton";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  MapPin,
  CloudUpload,
  Folder,
  Activity,
  Users,
  Shield,
  PlayCircle,
  StopCircle,
  Clock,
  CalendarDays,
  ChevronDown,
  Copy,
  RefreshCw,
  CheckCircle2,
  Send,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/trip")({
  head: () => pageMeta({ title: 'Trip Dashboard — Roava by WGP Apps', description: 'Your group trip in one place: photos, members, route, and memories.', path: '/trip' }),
  component: TripDashboard,
});

import { useTrackingState } from "@/lib/native/tracking-state";

const roavaWebsiteLogo = roavaWordmark;


function TripDashboard() {
  const { t: tx } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const membersFn = useServerFn(getTripMembers);
  const dashFn = useServerFn(getTripDashboard);
  const inviteFn = useServerFn(getTripInviteCode);
  const rotateInviteFn = useServerFn(regenerateTripInviteCode);
  const submitFn = useServerFn(submitMyTrip);
  const unsubmitFn = useServerFn(unsubmitMyTrip);

  const { isOffline } = useOfflineRedirect({ redirect: false });
  const cachedTrip = useCachedTrip();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
    retry: isOffline ? false : 1,
    refetchOnMount: "always",
    staleTime: 0,
  });
  const tripId = data?.trip?.id;
  const isAdmin = !!data?.isAdmin;
  const { data: membersData } = useQuery({
    queryKey: ["members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });
  const { data: dash } = useQuery({
    queryKey: ["trip-dashboard", tripId],
    queryFn: () => dashFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
  });
  const { data: inviteData, refetch: refetchInvite } = useQuery({
    queryKey: ["trip-invite-code", tripId],
    queryFn: () => inviteFn({ data: { tripId: tripId! } }),
    enabled: !!tripId && isAdmin,
    staleTime: 60_000,
  });
  const inviteCode = inviteData?.code ?? null;

  const copyInvite = async () => {
    if (!inviteCode) return;
    try {
      const url = shareUrl(`/join?code=${encodeURIComponent(inviteCode)}`);
      await navigator.clipboard.writeText(url);
      toast.success(tx("trip.toast_invite_copied"));
    } catch {
      toast.error(tx("trip.toast_copy_failed"));
    }
  };
  const rotateInvite = async () => {
    if (!tripId) return;
    try {
      await rotateInviteFn({ data: { tripId } });
      await refetchInvite();
      toast.success(tx("trip.toast_invite_rotated"));
    } catch (e: any) {
      toast.error(e?.message ?? tx("trip.toast_rotate_failed"));
    }
  };

  const tracking = useTrackingState();
  const [selectedMember, setSelectedMember] = useState<MemberLite | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [fixStuckOpen, setFixStuckOpen] = useState(false);

  // Defensive: if the user lands on Trip and tracking is genuinely off in
  // PERSISTED state, clear any orphan Live Activities left behind by a
  // previous session / crash. We intentionally do NOT depend on the
  // optimistic `tracking` hook value — it initializes synchronously to
  // false and would otherwise kill a live LA on every Trip remount while
  // tracking is actually running.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Short-circuit if the runtime says we're still tracking. The guarded
        // variant also checks persisted phase, but this avoids an unnecessary
        // bridge round-trip when we know locally that an active watch is live.
        const { isActiveWatchRunning, isActiveWatchPaused } =
          await import("@/lib/native/active-watch");
        if (isActiveWatchRunning() || isActiveWatchPaused()) return;
        if (cancelled) return;
        await endOrphanLiveActivitiesIfSafe({ source: "trip-mount" });
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);



  const mySubmittedAt = (data as any)?.mySubmittedAt as string | null | undefined;
  const allMembersSubmitted = membersData?.members && membersData.members.length > 0 && membersData.members.every((m) => m.submittedAt);

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!tripId) throw new Error("No trip");
      if (tracking) {
        try { await setTrackingState(false); } catch { /* non-fatal */ }
      }
      try { await stopActiveWatch({ source: "trip-submit" }); } catch { /* non-fatal */ }
      return submitFn({ data: { tripId } });
    },
    onSuccess: () => {
      toast.success(tx("trip.toast_trip_submitted"));
      setSubmitOpen(false);
      qc.invalidateQueries({ queryKey: ["my-trip"] });
      qc.invalidateQueries({ queryKey: ["trip-dashboard", tripId] });
      qc.invalidateQueries({ queryKey: ["members", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? tx("trip.toast_submit_failed")),
  });

  const unsubmitMut = useMutation({
    mutationFn: () => unsubmitFn({ data: { tripId: tripId! } }),
    onSuccess: () => {
      toast.success(tx("trip.toast_unsubmit_done"));
      qc.invalidateQueries({ queryKey: ["my-trip"] });
      qc.invalidateQueries({ queryKey: ["members", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? tx("trip.toast_unsubmit_failed")),
  });



  // Offline handling: never sit on "Loading trip…" forever, and never let
  // the route error boundary swallow a network failure when we have a cached
  // trip we can still operate on.
  const loadStartRef = useRef<number>(Date.now());
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (!isLoading) { setLoadingTimedOut(false); loadStartRef.current = Date.now(); return; }
    if (!isOffline) return;
    const t = window.setTimeout(() => setLoadingTimedOut(true), 1500);
    return () => window.clearTimeout(t);
  }, [isLoading, isOffline]);

  const shouldFallBack = isOffline && (isError || (isLoading && loadingTimedOut) || (!isLoading && !data?.trip));
  useEffect(() => {
    if (!shouldFallBack) return;
    if (cachedTrip) {
      console.info("[trip] offline using cached trip");
    } else {
      console.warn("[trip] offline no cached trip -> offline-control");
      nav({ to: "/offline-control", replace: true });
    }
  }, [shouldFallBack, cachedTrip, nav]);

  if (isLoading && !(isOffline && loadingTimedOut)) {
    return (
      <main className="min-h-screen bg-background">
        <div className="mx-auto max-w-[480px] px-5 pt-[calc(max(env(safe-area-inset-top),2.75rem)+1rem)]">
          <p className="text-muted-foreground">{tx("trip.loading")}</p>
        </div>
      </main>
    );
  }

  // Offline + cached trip → render a minimal offline view with controls to
  // jump into the dedicated offline screen. Avoids dead-state UI.
  if (shouldFallBack && cachedTrip) {
    return (
      <main className="min-h-screen bg-background">
        <div className="mx-auto max-w-[480px] px-5 pt-[calc(max(env(safe-area-inset-top),2.75rem)+1rem)] pb-28 space-y-4">
          <h1 className="text-[28px] leading-tight font-serif tracking-tight">{cachedTrip.name ?? tx("trip.your_trip")}</h1>
          <p className="text-[13px] text-muted-foreground">
            {tx("trip.offline_body")}
          </p>
          <Button onClick={() => nav({ to: "/offline-control" })} className="h-12 rounded-2xl w-full">
            {tx("trip.open_offline_controls")}
          </Button>
          <Button variant="outline" onClick={() => nav({ to: "/tracking" })} className="h-12 rounded-2xl w-full">
            {tx("trip.go_to_tracking")}
          </Button>
        </div>
      </main>
    );
  }
  if (!data?.trip) {
    return (
      <main className="min-h-screen bg-background">
        <div className="mx-auto max-w-[480px] px-5 pt-[calc(max(env(safe-area-inset-top),2.75rem)+1rem)] pb-28">
          <h1 className="text-[34px] leading-none font-serif tracking-tight">{tx("trip.welcome_title")}</h1>
          <p className="mt-2 text-[13.5px] text-muted-foreground">{tx("trip.welcome_body")}</p>
          <Button onClick={() => nav({ to: "/join", search: {} })} className="mt-6 h-14 rounded-2xl w-full text-base font-semibold">
            {tx("trip.enter_invite")}
          </Button>
        </div>
      </main>
    );
  }

  const trip = data.trip;
  const { dayLabel, daysRemaining } = computeTripDay(trip.startDate, trip.endDate, tx);
  const pending = dash?.my.pendingUploads ?? 0;

  return (
    <>
      <main className="min-h-screen bg-background">
        <div className="mx-auto max-w-[480px] pb-28 pt-[calc(max(env(safe-area-inset-top),2.75rem)+0.5rem)]">
          {/* Top utility bar removed */}

          {/* Title */}
          <div className="px-5 pt-2 pb-4">
            <img
              src={roavaWebsiteLogo}
              alt="Roava by WGP Apps"
              className="h-auto w-full max-w-[180px]"
              style={{ mixBlendMode: "multiply" }}
            />

            <div className="mt-4 flex items-center justify-between gap-3">
              <h1 className="text-[26px] leading-tight font-serif tracking-tight line-clamp-2 min-w-0 flex-1">{trip.name}</h1>
              <div className="shrink-0"><TripSwitcher /></div>
            </div>

            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <p className="text-[13.5px] text-muted-foreground">
                {formatDate(trip.startDate)} — {formatDate(trip.endDate)}
              </p>
              {mySubmittedAt && (
                <span className="inline-flex items-center gap-1 rounded-full bg-success/15 text-success border border-success/30 px-2 py-0.5 text-[11px] font-medium">
                  <CheckCircle2 className="h-3 w-3" /> {tx("trip.submitted_chip")}
                </span>
              )}
            </div>
          </div>

          {/* Hero countdown */}
          <div className="px-5">
            <div className="rounded-[18px] border border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-4">
              {allMembersSubmitted && (
                <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-success/20 text-success border border-success/40 px-3 py-1 text-[12px] font-semibold">
                  <CheckCircle2 className="h-4 w-4" /> {tx("trip.trip_complete_chip")}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="h-5 w-5 rounded-[5px] bg-primary/20 grid place-items-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                </span>
                <p className="text-[11px] uppercase tracking-[0.12em] text-primary/90 font-medium">
                  {dayLabel}
                </p>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-[40px] font-semibold leading-none tabular text-foreground">
                  {daysRemaining > 0 ? daysRemaining : daysRemaining === 0 ? 0 : "—"}
                </span>
                <span className="text-[13px] text-muted-foreground">
                  {daysRemaining > 0 ? tx("trip.days_to_go") : daysRemaining === 0 ? tx("trip.last_day") : tx("trip.trip_complete_lower")}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
                <HeroStat label={tx("trip.hero_tracking")} value={tracking ? tx("trip.hero_on") : tx("trip.hero_off")} tone={tracking ? "success" : undefined} />
                <HeroStat
                  label={tx("trip.hero_uploads")}
                  value={pending === 0 ? tx("trip.hero_idle") : tx("trip.hero_pending", { n: pending })}
                  tone={pending > 0 ? "warn" : "success"}
                  onClick={pending > 0 ? () => setFixStuckOpen(true) : undefined}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <TrackingHealthChip />
              </div>


              <div className="mt-3 flex items-center justify-between gap-2">
                <Link
                  to="/settings/privacy"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                >
                  {tx("trip.privacy_sharing")}
                </Link>
                <SaveTripToHealthButton tripId={trip.id} startISO={trip.startDate} endISO={trip.endDate} />
              </div>

              {isAdmin && (
                <div className="mt-3 rounded-lg bg-background/40 border border-border/60 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground/80">
                        {tx("trip.admin_invite_label")}
                      </p>
                      <p className="mt-0.5 font-mono text-[15px] font-semibold tabular tracking-widest text-foreground truncate">
                        {inviteCode ?? "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={copyInvite}
                        disabled={!inviteCode}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1.5 text-[11.5px] font-medium text-foreground active:opacity-70 disabled:opacity-40"
                        aria-label={tx("trip.copy_aria")}
                      >
                        <Copy className="h-3.5 w-3.5" /> {tx("trip.copy")}
                      </button>
                      <button
                        type="button"
                        onClick={rotateInvite}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1.5 text-[11.5px] font-medium text-muted-foreground active:opacity-70"
                        aria-label={tx("trip.rotate_aria")}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Primary actions */}
          <div className="px-5 pt-4 grid gap-2">
            {tracking ? (
              <ActionButton
                Icon={StopCircle}
                label={tx("trip.stop_tracking")}
                onClick={() => nav({ to: "/tracking" })}
                tone="destructive"
              />
            ) : (
              <ActionButton
                Icon={PlayCircle}
                label={tx("trip.start_tracking")}
                onClick={() => nav({ to: "/tracking" })}
                tone="primary"
              />
            )}
            {tracking && tripId && <ManualGpsButton tripId={tripId} className="h-12 w-full rounded-2xl text-sm font-medium" />}
            <ActionButton Icon={CalendarDays} label={tx("trip.view_itinerary")} onClick={() => nav({ to: "/itinerary" })} />

            {mySubmittedAt ? (
              <div className="rounded-2xl border border-success/30 bg-success/10 px-4 py-3 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{tx("trip.you_submitted")}</p>
                  <p className="text-[11.5px] text-muted-foreground">
                    {formatDate(mySubmittedAt)}
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      disabled={unsubmitMut.isPending}
                      className="text-[12.5px] font-medium text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      {tx("trip.undo_submit")}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{tx("trip.undo_title")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {tx("trip.undo_body")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tx("trip.keep_submitted")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => unsubmitMut.mutate()}>
                        {tx("trip.undo_submit")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : (
              <ActionButton
                Icon={Send}
                label={tx("trip.submit_trip")}
                onClick={() => setSubmitOpen(true)}
                tone="primary"
              />
            )}
          </div>

          {/* Tile grid — quick actions + my totals */}
          <div className="px-5 pt-4 grid grid-cols-2 gap-3">
            <HubTile
              Icon={CloudUpload}
              label={tx("trip.tile_upload")}
              caption={tx("trip.tile_upload_caption")}
              tint="bg-primary/15"
              iconColor="text-primary"
              onClick={() => nav({ to: "/upload" })}
            />
            <HubTile
              Icon={Folder}
              label={tx("trip.tile_my_uploads")}
              caption={tx("trip.tile_my_uploads_caption")}
              tint="bg-muted"
              iconColor="text-foreground"
              onClick={() => nav({ to: "/my-uploads" })}
            />
            {(() => {
              const trackingNow = tracking;
              const f = freshness(dash?.my.lastLocationAt, { liveUnderMin: 2, staleUnderMin: 10 });
              return (
                <HubTile
                  Icon={MapPin}
                  label={tx("trip.tile_last_location")}
                  caption={tx("trip.tile_last_location_caption")}
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`inline-block h-2 w-2 rounded-full ${trackingNow ? f.dotClass : "bg-muted-foreground/50"}`} />
                      <span className={`text-[15px] font-semibold ${trackingNow ? f.textClass : "text-foreground"}`}>{f.label}</span>
                    </span>
                  }
                  tint="bg-emerald-500/15"
                  iconColor="text-emerald-500"
                  onClick={() => nav({ to: "/tracking" })}
                />
              );
            })()}

            <HubTile
              Icon={Clock}
              label={tx("trip.tile_last_upload")}
              value={relTime(dash?.my.lastUploadAt, tx)}
              tint="bg-amber-500/15"
              iconColor="text-amber-500"
              onClick={() => nav({ to: "/my-uploads" })}
            />
          </div>

          <div className="px-5 pt-4 grid gap-3">
            {(() => {
              const gap = dash?.my.todayBiggestGap;
              if (!gap) return null;
              const isLikelyFlight = gap.distanceKm >= 100 && gap.durationMin >= 20;
              const startClock = new Date(gap.startedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
              const endClock = new Date(gap.endedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
              const km = gap.distanceKm >= 100 ? Math.round(gap.distanceKm) : Math.round(gap.distanceKm * 10) / 10;
              return (
                <button
                  type="button"
                  onClick={() => nav({ to: "/library", search: {} })}
                  className="text-left rounded-2xl border border-border bg-card/70 p-4 active:opacity-80 transition"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{isLikelyFlight ? "✈️" : "⏸️"}</span>
                    <p className="font-semibold text-[14px]">
                      {isLikelyFlight ? tx("trip.gap_flight_title") : tx("trip.gap_title")}
                    </p>
                  </div>
                  <p className="mt-1 text-[12.5px] text-muted-foreground leading-snug">
                    {startClock} → {endClock} · {gap.durationMin} {tx("trip.gap_minutes")}{isLikelyFlight ? ` · ${km} km` : ""}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">
                    {tx("trip.gap_during", { before: gap.beforeCount.toLocaleString(), after: gap.afterCount.toLocaleString() })}
                  </p>
                </button>
              );
            })()}
            <CollapseCard Icon={Activity} title={tx("trip.today_activity")}>
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label={tx("trip.stat_locations")} value={dash?.my.todayLocations ?? 0} onClick={() => nav({ to: "/tracking" })} />
                <MiniStat label={tx("trip.stat_photos")} value={dash?.my.todayPhotos ?? 0} onClick={() => nav({ to: "/my-uploads" })} />
                <MiniStat label={tx("trip.stat_videos")} value={dash?.my.todayVideos ?? 0} onClick={() => nav({ to: "/my-uploads" })} />
                <MiniStat label={tx("trip.stat_pending")} value={dash?.my.pendingUploads ?? 0} onClick={() => nav({ to: "/queue" })} />
              </div>
              {(() => {
                const f = freshness(dash?.my.lastLocationAt, { liveUnderMin: 5, staleUnderMin: 20 });
                if (f.state === "unknown" || f.state === "live") return null;
                return (
                  <p className="mt-2 text-[11.5px] text-muted-foreground">
                    {tx("trip.last_fix", { clock: f.clock, rel: f.rel })}
                  </p>
                );
              })()}
            </CollapseCard>



            <CollapseCard Icon={Users} title={tx("trip.group_progress")}>
              <div className="grid grid-cols-2 gap-2">
                {isAdmin && (
                  <>
                    <MiniStat label={tx("trip.stat_members")} value={dash?.group.totalMembers ?? 0} onClick={() => nav({ to: "/permissions" })} />
                    <MiniStat label={tx("trip.stat_active_24h")} value={dash?.group.activeTrackers ?? 0} onClick={() => nav({ to: "/tracking" })} />
                  </>
                )}
                <MiniStat label={tx("trip.stat_group_photos")} value={dash?.group.groupPhotos ?? 0} onClick={() => nav({ to: "/library", search: {} })} />
                <MiniStat label={tx("trip.stat_group_videos")} value={dash?.group.groupVideos ?? 0} onClick={() => nav({ to: "/library", search: {} })} />
                {isAdmin && (
                  <MiniStat
                    label={tx("trip.stat_uploaded")}
                    value={dash ? `${(dash.group.totalBytes / 1e9).toFixed(2)} GB` : "—"}
                    wide
                    onClick={() => nav({ to: "/library", search: {} })}
                  />
                )}
              </div>
            </CollapseCard>
          </div>

          {/* Travelers */}
          <div className="mt-6 px-5">
            <CollapseCard Icon={Users} title={tx("trip.travelers")}>
              <div className="grid gap-2">
                {(membersData?.members ?? []).map((m) => (
                  <button
                    key={m.userId}
                    type="button"
                    onClick={() => setSelectedMember({
                      userId: m.userId,
                      displayName: m.displayName,
                      avatarUrl: m.avatarUrl,
                      joinedAt: m.joinedAt,
                    })}
                    className="flex items-center gap-3 rounded-2xl bg-card border border-border p-3 text-left transition-colors hover:bg-muted/50 active:bg-muted/70 focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <div className="size-10 rounded-full bg-accent grid place-items-center text-accent-foreground font-semibold overflow-hidden">
                      {m.avatarUrl ? (
                        <img src={m.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (m.displayName ?? "?").slice(0, 1).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{m.displayName}</p>
                      <p className="text-xs text-muted-foreground">{tx("trip.joined_on", { date: formatDate(m.joinedAt) })}</p>
                    </div>
                  </button>
                ))}
              </div>
            </CollapseCard>
          </div>

          <Link
            to="/permissions"
            className="mt-6 mx-5 flex items-center justify-center gap-2 text-sm text-muted-foreground py-3"
          >
            <Shield className="size-4" /> {tx("trip.manage_permissions")}
          </Link>

          {tripId && (
            <div className="px-5 pt-2" data-tour="row.familyFollow">
              <FamilyFollowCard tripId={tripId} />
            </div>
          )}


          {isAdmin && (
            <div className="px-5 pt-2">
              <Link
                to="/admin"
                className="flex items-center justify-center gap-2 rounded-[14px] border border-border bg-card/50 px-4 py-3 text-[13.5px] text-muted-foreground active:opacity-80"
              >
                <Shield className="h-4 w-4" /> {tx("trip.admin_dashboard")}
              </Link>
            </div>
          )}
        </div>
      </main>

      <MemberDetailSheet
        member={selectedMember}
        isAdmin={isAdmin}
        onClose={() => setSelectedMember(null)}
      />

      <SubmitTripDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        isTracking={!!tracking}
        pendingUploads={pending}
        onConfirm={() => submitMut.mutate()}
        isSubmitting={submitMut.isPending}
      />

      <FixStuckUploadsSheet
        open={fixStuckOpen}
        onOpenChange={setFixStuckOpen}
        tripId={tripId ?? null}
        pending={pending}
      />
    </>

  );
}

function HeroStat({ label, value, tone, onClick }: { label: string; value: string; tone?: "success" | "warn"; onClick?: () => void }) {
  const valueCls =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
      ? "text-amber-600 dark:text-amber-400"
      : "text-foreground";
  const inner = (
    <>
      <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground/80">{label}</p>
      <p className={`mt-0.5 text-[14px] font-semibold leading-none ${valueCls}`}>{value}</p>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left rounded-lg bg-background/40 border border-border/60 px-2.5 py-2 hover:bg-background/60 active:bg-background/80 transition"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="rounded-lg bg-background/40 border border-border/60 px-2.5 py-2">
      {inner}
    </div>
  );
}

function HubTile({
  Icon,
  label,
  caption,
  value,
  tint,
  iconColor,
  onClick,
}: {
  Icon: any;
  label: string;
  caption?: string;
  value?: React.ReactNode;
  tint: string;
  iconColor: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[18px] border border-border bg-card/70 p-4 min-h-[120px] flex flex-col text-left active:opacity-80 transition"
    >
      <span className={`h-10 w-10 rounded-[10px] grid place-items-center ${tint}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </span>
      <div className="mt-auto pt-3">
        {value !== undefined && (
          <p className="text-[20px] font-semibold tabular leading-none text-foreground">{value}</p>
        )}
        <p className={`text-[15px] font-semibold text-foreground leading-tight ${value !== undefined ? "mt-1" : ""}`}>
          {label}
        </p>
        {caption && (
          <p className="text-[12px] text-muted-foreground leading-tight mt-0.5">{caption}</p>
        )}
      </div>
    </button>
  );
}

function ActionButton({
  Icon,
  label,
  onClick,
  tone = "default",
}: {
  Icon: any;
  label: string;
  onClick: () => void;
  tone?: "default" | "primary" | "destructive";
}) {
  const cls =
    tone === "primary"
      ? "bg-primary text-primary-foreground"
      : tone === "destructive"
      ? "bg-destructive text-destructive-foreground"
      : "bg-card border border-border text-foreground";
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 rounded-2xl px-4 h-14 text-left font-semibold active:scale-[0.99] transition ${cls}`}
    >
      <Icon className="size-5" />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function CollapseCard({ Icon, title, children }: { Icon: any; title: string; children: React.ReactNode }) {
  return (
    <Collapsible className="rounded-2xl border border-border bg-card">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 px-4 py-3 text-left">
        <Icon className="size-4 text-muted-foreground" />
        <span className="font-semibold flex-1">{title}</span>
        <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function MiniStat({ label, value, wide, onClick }: { label: string; value: React.ReactNode; wide?: boolean; onClick?: () => void }) {
  const className = `text-left rounded-xl bg-muted/40 p-3 ${wide ? "col-span-2" : ""} ${onClick ? "transition-colors hover:bg-muted/70 active:bg-muted/90 focus:outline-none focus:ring-2 focus:ring-ring" : ""}`;
  const content = (
    <>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold mt-0.5">{value}</p>
    </>
  );
  if (onClick) {
    return <button type="button" onClick={onClick} className={className}>{content}</button>;
  }
  return <div className={className}>{content}</div>;
}

function formatDate(s: string) {
  return formatLocalDate(s);
}

function computeTripDay(startISO: string, endISO: string, tx: TFunction) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = parseLocalDate(startISO);
  start.setHours(0, 0, 0, 0);
  const end = parseLocalDate(endISO);
  end.setHours(0, 0, 0, 0);
  const totalDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const dayNum = Math.round((today.getTime() - start.getTime()) / 86400000) + 1;
  const daysRemaining = Math.round((end.getTime() - today.getTime()) / 86400000);
  let dayLabel: string;
  if (today < start) dayLabel = tx("trip.starts_in_days", { n: Math.round((start.getTime() - today.getTime()) / 86400000) });
  else if (today > end) dayLabel = tx("trip.trip_complete");
  else dayLabel = tx("trip.day_of", { n: dayNum, total: totalDays });
  return { dayLabel, daysRemaining };
}

function relTime(iso: string | null | undefined, tx: TFunction) {
  if (!iso) return tx("trip.rel_none");
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return tx("trip.rel_just_now");
  if (mins < 60) return tx("trip.rel_min", { n: mins });
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return tx("trip.rel_hour", { n: hrs });
  const days = Math.round(hrs / 24);
  return tx("trip.rel_day", { n: days });
}

